import {Component, forwardRef, ViewEncapsulation, EventEmitter,
  ElementRef, ViewChild, HostBinding} from '@angular/core';
import {NG_VALUE_ACCESSOR, ControlValueAccessor, NgModel, FormGroup} from '@angular/forms';
import {Input, Output} from '@angular/core';
import {Observable} from 'rxjs';
// TODO: ld-tooltip will be replaced by ula-tooltip; this is a breaking change
import { TooltipContentComponent } from '../tooltip';


const noop = () => {
};


export const GENERIC_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => UlaSelectStateComponent),
  multi: true
};


let nextUniqueId = 0;

// More reading: https://github.com/angular/material2/blob/master/src/lib/input/input.ts
// More reading: http://almerosteyn.com/2016/04/linkup-custom-control-to-ngcontrol-ngmodel
@Component({
  selector: 'ula-select-state',
  templateUrl: './ula-select-state.template.html',
  styleUrls: ['./ula-input.style.css'],
  providers: [GENERIC_INPUT_CONTROL_VALUE_ACCESSOR],
  encapsulation: ViewEncapsulation.None
})
export class UlaSelectStateComponent implements ControlValueAccessor {
  // omitting the '_class' variable name doesn't seem to correctly apply the CSS class,
  // so don't omit it
  @HostBinding('class') _class = 'd-block';

  public counter: number = nextUniqueId++;
  public _focused: boolean = false;
  public _value: any = '';

  private _onTouchedCallback: () => void = noop;
  private _onChangeCallback: (_: any) => void = noop;

  /** Readonly properties. */
  get inputName(): string {
    return `${this.name}${this.counter}`;
  }

  @Input() validationForm: FormGroup;
  @Input() displayName: string;
  @Input() formControlName: string;
  @Input() shouldCheckIsDirty: boolean = false;
  @Input() hideError: boolean = false;

  /**
   * Misc
   */
  @Input() label: string;
  @Input() defaultTitle: string = '';
  @Input() defaultValue: any = null;
  // TODO: ld-tooltip will be replaced by ula-tooltip; this is a breaking change
  @Input() popover: string | TooltipContentComponent;
  @Input() options: any;
  @Input() title: string = '';

  /**
   * Bindings.
   */
  @Input() id: string = `ula-select-state${this.counter}`;
  @Input() name: string = null;
  @Input() qa: string;

  private _disabled: boolean = false;
  private _required: boolean = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value) {
    this._disabled = this._coerceBooleanProperty(value);
  }

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(value) {
    this._required = this._coerceBooleanProperty(value);
  }

  /** Readonly properties. */
  get empty() { return (this._value == null || this._value === ''); }

  private _blurEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  private _focusEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  @Output('blur')
  get onBlur(): Observable<FocusEvent> {
    return this._blurEmitter.asObservable();
  }

  @Output('focus')
  get onFocus(): Observable<FocusEvent> {
    return this._focusEmitter.asObservable();
  }

  get value(): any {
    return this._value;
  };

  @Input() set value(v: any) {
    this._value = v;
    this._onChangeCallback(v);
  }

  @ViewChild('select') _inputElement: ElementRef;
  @ViewChild('inputModel') inputModel: NgModel;

  constructor() {}

  hasErrors() {
    return this.validationForm &&
      this.validationForm.controls[this.formControlName].errors &&
      (this.validationForm.controls[this.formControlName].touched ||
      !this.shouldCheckIsDirty) &&
      !this._focused;
  }

  /** Set focus on input */
  focus() {
    this._inputElement.nativeElement.focus();
  }

  _handleFocus(event: FocusEvent) {
    this._focused = true;
    this._focusEmitter.emit(event);
  }

  _handleBlur(event: FocusEvent) {
    this._focused = false;
    this._onTouchedCallback();
    this._blurEmitter.emit(event);
  }

  _handleChange(event: Event) {
    this.value = (<HTMLInputElement>event.target).value;
    this._onTouchedCallback();
  }

  /**
   * Implemented as part of ControlValueAccessor.
   * TODO: internal
   */
  writeValue(value: any) {
    // set undefined values back to null
    if (typeof value === 'undefined' || !value) {
      value = null;
    }
    this._value = value;
  }

  /**
   * Implemented as part of ControlValueAccessor.
   * TODO: internal
   */
  registerOnChange(fn: any) {
    this._onChangeCallback = fn;
  }

  /**
   * Implemented as part of ControlValueAccessor.
   * TODO: internal
   */
  registerOnTouched(fn: any) {
    this._onTouchedCallback = fn;
  }

  private _coerceBooleanProperty(value: any): boolean {
    return value != null && `${value}` !== 'false';
  }
}
